/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { isRequestFailure, useFetch } from '../../../hooks/useFetch';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { TextAreaInputForm } from '../../inputs/forms/TextAreaInputForm';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';

interface WeekMeetingFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function WeekMeetingForm({ defaultValues = {} }: WeekMeetingFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/contents';
  const { requestApi } = useFetch(baseUrl);
  const { itemId, moduleId, weekId } = useParams();
  const navigate = useNavigate();

  const optionsUserType = [
    {
      label: 'Professor',
      value: 'PROFESSOR',
    },
    {
      label: 'Responsável',
      value: 'RESPONSAVEL',
    },
    {
      label: 'Todos',
      value: 'TODOS',
    },
  ];
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do encontro',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'title',
      value: defaultValues.title ?? '',
      placeholder: 'Título do encontro',
      label: 'Título',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'user_type',
      value: defaultValues?.user_type
        ? optionsUserType.find((item) => item.value === defaultValues?.user_type)
        : optionsUserType.find((item) => item.value === 'TODOS'),
      placeholder: 'Selecione o tipo de usuário que receberá',
      label: 'Usuários que vão receber',
      yup: Yup.mixed(),
    },
    {
      name: 'description',
      value: defaultValues.description ?? '',
      placeholder: 'Descrição do encontro',
      label: 'Descrição',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .test('max-length', 'Máximo de 18 mil caracteres', (value) => {
          const maxLength = 18000;
          if (!value) return true;
          if (value.length > maxLength) return false;
          return true;
        }),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const title = formInputs.find((field) => field.name === 'title');
  const description = formInputs.find((field) => field.name === 'description');
  const status = formInputs.find((field) => field.name === 'status');
  const userType = formInputs.find((field) => field.name === 'user_type');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      week_id: weekId,
      user_type: values.user_type.value,
    };
    const result = await requestApi(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if (!isRequestFailure(result))
      navigate(`/contents/modules/${moduleId}/weeks/${weekId}/meetings`);
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  const backTo = `/contents/modules/${moduleId}/weeks/${weekId}/meetings`;
  const isEdit = Boolean(defaultValues.name);

  return (
    <FormShell
      title={isEdit ? 'Editar Encontro' : 'Criar Encontro'}
      backTo={backTo}
      backLabel="Encontros"
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações básicas">
          <FormRow gridTemplateColumns={{ base: '1fr', md: '3fr 3fr 1fr' }}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <InputForm
              name={title?.name}
              label={title?.label}
              placeholder={title?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </FormRow>
          <SelectInputForm
            name={userType?.name}
            label={userType?.label}
            options={optionsUserType}
            placeholder={userType?.placeholder}
            control={control}
            errors={errors}
          />
          <TextAreaInputForm
            name={description?.name}
            label={description?.label}
            placeholder={description?.placeholder}
            errors={errors}
            register={register}
          />
        </FormSection>

        <FormActions cancelTo={backTo} isLoading={isSubmitting} />
      </Box>
    </FormShell>
  );
}
